import React, { useEffect, useRef, useState } from 'react';
import { SunLoader } from './SunLoader.js';
import { useFillToViewport } from './Loader.js';

/** Сколько длится уход знака, когда результаты пришли (см. `.chooser-search.is-leaving`). */
const LEAVE_MS = 320;

/**
 * Поиск источников или раздач — крупным знаком «три тела» по ЦЕНТРУ пустой
 * области под заголовком, с тихой подписью под ним (правка владельца
 * 03.10.2026). Маленькое солнце в строке у левого края терялось: пустой экран
 * с крошечной строкой в углу читался как «ничего не нашлось».
 *
 * Это и есть образец загрузчика области (`Loader`, вариант `area`): тот же
 * размер знака, та же подпись, та же высота до низа окна. Отличие одно —
 * уход: результаты пришли — знак растворяется поверх них и уходит (без
 * движения, если оно выключено), а не исчезает рывком; места он при этом уже
 * не занимает, и список не прыгает.
 */
export function ChooserSearch({ active, caption }: { active: boolean; caption: string }): React.JSX.Element | null {
  const [present, setPresent] = useState(active);
  const [leaving, setLeaving] = useState(false);
  const host = useRef<HTMLDivElement>(null);

  // Смена `active` во время отрисовки, а не в эффекте: так первый же кадр
  // после ответа уже уводит знак из раскладки.
  const [wasActive, setWasActive] = useState(active);
  if (wasActive !== active) {
    setWasActive(active);
    if (active) {
      setPresent(true);
      setLeaving(false);
    } else if (present) {
      setLeaving(true);
    }
  }

  useEffect(() => {
    if (!leaving) return undefined;
    const timer = window.setTimeout(() => {
      setPresent(false);
      setLeaving(false);
    }, LEAVE_MS);
    return () => window.clearTimeout(timer);
  }, [leaving]);

  const minHeight = useFillToViewport(host, present && !leaving);

  if (!present) return null;
  return (
    <div
      ref={host}
      className={leaving ? 'loader loader-area chooser-search is-leaving' : 'loader loader-area chooser-search'}
      style={minHeight !== null ? { minHeight } : undefined}
      role="status"
    >
      <SunLoader className="loader-sign" />
      <span className="loader-caption">{caption}</span>
    </div>
  );
}
